import { useGeld } from '../lib/geld';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2, Undo2, Thermometer, Clock, CalendarOff, ArrowLeftRight, PenLine, MapPin, Coffee, type LucideIcon,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn, formatMoney } from '../lib/utils';
import type { AfwijkingType, ShiftWithCalc } from '../types';
import { ICON, Notice } from './ui';

/**
 * Afwijking per dienst. Uitgangspunt: geen afwijking = gewerkt zoals gepland.
 * Ging er iets anders (later begonnen, langer gebleven, geen pauze, ander object, of het klopt
 * niet met Trigion), dan meld je dat hier. Loon en reiskosten worden direct herberekend; de
 * melding komt terug op /afwijkingen en in de mail aan de planner.
 */

export const AFWIJKING_LABEL: Record<AfwijkingType, { label: string; icon: LucideIcon }> = {
  ziek: { label: 'Ziek', icon: Thermometer },
  verlof: { label: 'Verlof', icon: CalendarOff },
  geruild: { label: 'Geruild', icon: ArrowLeftRight },
  tijden: { label: 'Andere tijden', icon: Clock },
  object: { label: 'Ander object', icon: MapPin },
  pauze: { label: 'Pauze anders', icon: Coffee },
  anders: { label: 'Klopt niet met Trigion', icon: PenLine },
};

export function afwijkingInfo(type: AfwijkingType | null | undefined) {
  return type ? { type, ...AFWIJKING_LABEL[type] } : null;
}

type Keuze = 'gewerkt' | 'ziek' | 'verlof' | 'geruild';
const KEUZES: Array<{ value: Keuze; label: string; icon: LucideIcon }> = [
  { value: 'gewerkt', label: 'Iets ging anders', icon: PenLine },
  { value: 'ziek', label: 'Ziek', icon: Thermometer },
  { value: 'verlof', label: 'Verlof', icon: CalendarOff },
  { value: 'geruild', label: 'Geruild', icon: ArrowLeftRight },
];

const VERLOF_SOORTEN = [
  { value: 'vakantie', label: 'Vakantieverlof' },
  { value: 'bijzonder_verlof', label: 'Bijzonder verlof (bijv. overlijden)' },
  { value: 'buitengewoon', label: 'Buitengewoon verlof' },
] as const;

const PAUZES = [0, 15, 30, 45, 60];

/** Korte beschrijving van wat er anders was, voor in lijsten. */
export function afwijkingDetails(s: {
  start_time: string;
  end_time: string;
  actual_start_time?: string | null;
  actual_end_time?: string | null;
  actual_pauze_min?: number | null;
  planned_object_naam?: string | null;
  object_naam_snapshot?: string | null;
}): string[] {
  const out: string[] = [];
  if (s.actual_start_time && s.actual_end_time) out.push(`Gewerkt ${s.actual_start_time}–${s.actual_end_time} (gepland ${s.start_time}–${s.end_time})`);
  if (s.actual_pauze_min != null) out.push(s.actual_pauze_min === 0 ? 'Geen pauze gehad' : `${s.actual_pauze_min} min pauze`);
  if (s.planned_object_naam && s.object_naam_snapshot) out.push(`Bij ${s.object_naam_snapshot} in plaats van ${s.planned_object_naam}`);
  return out;
}

export function AfwijkingPanel({ shift }: { shift: ShiftWithCalc }) {
  const { geld, label } = useGeld();
  const qc = useQueryClient();
  const current = afwijkingInfo(shift.deviation_reason);
  const plannedObjectId = shift.planned_object_id ?? shift.object_id;
  const [keuze, setKeuze] = useState<Keuze | null>(null);
  const [start, setStart] = useState(shift.actual_start_time ?? shift.start_time);
  const [end, setEnd] = useState(shift.actual_end_time ?? shift.end_time);
  const [pauze, setPauze] = useState<string>(shift.actual_pauze_min != null ? String(shift.actual_pauze_min) : '');
  const [objectId, setObjectId] = useState<string>(String(shift.object_id ?? ''));
  const [verlofSoort, setVerlofSoort] = useState<(typeof VERLOF_SOORTEN)[number]['value']>(
    shift.leave_type && shift.leave_type !== 'work' && shift.leave_type !== 'ziekte' ? shift.leave_type : 'vakantie'
  );
  const [notes, setNotes] = useState(shift.deviation_notes ?? '');

  const { data: objects } = useQuery({ queryKey: ['objects'], queryFn: api.objects.list, enabled: keuze === 'gewerkt' });

  const invalidate = () => {
    for (const k of ['shifts', 'shift', 'dashboard', 'compare', 'afwijkingen']) qc.invalidateQueries({ queryKey: [k] });
  };

  const setMut = useMutation({
    mutationFn: (type: Keuze) =>
      api.shifts.setAfwijking(shift.id, {
        type,
        actual_start_time: type === 'gewerkt' ? start : null,
        actual_end_time: type === 'gewerkt' ? end : null,
        pauze_min: type === 'gewerkt' && pauze !== '' ? Number(pauze) : null,
        object_id: type === 'gewerkt' && objectId ? Number(objectId) : null,
        verlof_soort: type === 'verlof' ? verlofSoort : undefined,
        notes: notes.trim() || null,
      }),
    onSuccess: () => {
      setKeuze(null);
      invalidate();
    },
  });

  const clearMut = useMutation({
    mutationFn: () => api.shifts.clearAfwijking(shift.id),
    onSuccess: () => {
      setNotes('');
      setPauze('');
      invalidate();
    },
  });

  // Verlof/ziekte die uit het rooster of AFAS kwam (zonder eigen afwijking) telt ook als afwijking.
  const roosterLeave = !current && shift.leave_type && shift.leave_type !== 'work';
  const details = afwijkingDetails({
    ...shift,
    planned_object_naam:
      shift.planned_object_id != null ? objects?.find((o) => o.id === shift.planned_object_id)?.naam ?? 'het geplande object' : null,
  });
  const CurrentIcon = current?.icon;
  const error = (setMut.error || clearMut.error) as Error | null;

  return (
    <div className="space-y-3">
      <h3>Afwijking</h3>

      {current && CurrentIcon ? (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2.5">
          <CurrentIcon className={cn(ICON.sm, 'mt-0.5 shrink-0 text-amber-200')} aria-hidden />
          <div className="min-w-0 flex-1 space-y-0.5 text-sm">
            <div className="font-medium text-amber-100">{current.label}</div>
            {details.map((d) => (
              <div key={d} className="text-slate-300">
                {d}
              </div>
            ))}
            {shift.ziekte && <div className="text-slate-300">{shift.ziekte.uitleg}</div>}
            {shift.deviation_notes && <div className="text-slate-400">{shift.deviation_notes}</div>}
            <div className="text-slate-300">
              {label} <span className="t-num font-medium text-money">{geld(shift.total_gross, shift.shift_date)}</span>
            </div>
          </div>
          <button onClick={() => clearMut.mutate()} disabled={clearMut.isPending} className="btn-ghost btn-sm shrink-0">
            <Undo2 className={ICON.sm} aria-hidden /> Weghalen
          </button>
        </div>
      ) : roosterLeave ? (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2.5 text-sm">
          {shift.leave_type === 'ziekte' ? (
            <Thermometer className={cn(ICON.sm, 'mt-0.5 shrink-0 text-amber-200')} aria-hidden />
          ) : (
            <CalendarOff className={cn(ICON.sm, 'mt-0.5 shrink-0 text-amber-200')} aria-hidden />
          )}
          <div>
            <div className="font-medium text-amber-100">In het rooster als {shift.leave_type === 'ziekte' ? 'ziekte' : 'verlof'}</div>
            <div className="text-slate-300">
              {label} <span className="t-num font-medium text-money">{geld(shift.total_gross, shift.shift_date)}</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <CheckCircle2 className={cn(ICON.sm, 'text-money')} aria-hidden /> Gewerkt zoals gepland
        </div>
      )}

      <div className="grid grid-cols-2 gap-2" role="group" aria-label="Wat was er anders?">
        {KEUZES.map((k) => {
          const active = keuze === k.value;
          return (
            <button
              key={k.value}
              type="button"
              onClick={() => {
                if (k.value === 'ziek' || k.value === 'geruild') setMut.mutate(k.value);
                else setKeuze(active ? null : k.value);
              }}
              disabled={setMut.isPending}
              aria-pressed={active}
              className={cn(
                'flex h-11 items-center gap-2 rounded-lg border px-3 text-left text-sm font-medium transition-colors duration-fast ease-calm active:scale-[0.98] disabled:opacity-50',
                active
                  ? 'border-slate-300 bg-surface-raised text-slate-50'
                  : 'border-surface-line text-slate-200 hover:border-slate-600 hover:bg-surface-raised'
              )}
            >
              <k.icon className={cn(ICON.sm, 'shrink-0', active ? 'text-slate-100' : 'text-slate-400')} aria-hidden />
              {k.label}
            </button>
          );
        })}
      </div>

      {keuze === 'gewerkt' && (
        <div className="animate-enter space-y-4 rounded-lg border border-surface-line p-3">
          <p className="text-sm text-slate-400">Vul alleen in wat anders was dan gepland.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="afw-start">Begonnen om</label>
              <input id="afw-start" type="time" step="300" className="input" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div>
              <label className="label" htmlFor="afw-end">Gestopt om</label>
              <input id="afw-end" type="time" step="300" className="input" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="afw-pauze">Pauze</label>
              <select id="afw-pauze" className="input" value={pauze} onChange={(e) => setPauze(e.target.value)}>
                <option value="">Zoals gepland</option>
                {PAUZES.map((m) => (
                  <option key={m} value={m}>
                    {m === 0 ? 'Geen pauze gehad' : `${m} minuten`}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label" htmlFor="afw-object">Object</label>
              <select id="afw-object" className="input" value={objectId} onChange={(e) => setObjectId(e.target.value)}>
                {(objects ?? []).map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.naam}
                    {o.id === plannedObjectId ? ' (gepland)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label" htmlFor="afw-notes">Toelichting</label>
            <textarea
              id="afw-notes"
              className="input"
              rows={2}
              placeholder="Bijvoorbeeld: aflossing was een half uur te laat"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <button onClick={() => setMut.mutate('gewerkt')} disabled={setMut.isPending} className="btn-primary w-full">
            {setMut.isPending ? 'Opslaan…' : 'Afwijking opslaan'}
          </button>
        </div>
      )}

      {keuze === 'verlof' && (
        <div className="animate-enter space-y-3 rounded-lg border border-surface-line p-3">
          <div>
            <label className="label" htmlFor="afw-verlof">Soort verlof</label>
            <select id="afw-verlof" className="input" value={verlofSoort} onChange={(e) => setVerlofSoort(e.target.value as typeof verlofSoort)}>
              {VERLOF_SOORTEN.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="afw-vnotes">Toelichting</label>
            <textarea id="afw-vnotes" className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <button onClick={() => setMut.mutate('verlof')} disabled={setMut.isPending} className="btn-primary w-full">
            Verlof opslaan
          </button>
        </div>
      )}

      {error && <Notice tone="err">{error.message}</Notice>}
    </div>
  );
}
